import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { FovButton, SpinAnimation } from 'components/generic';
import { LoadingIcon } from 'components/icons';
import { VerticalSpacer } from 'components/layout';
import { ProgressRail } from './ProgressRail';
import { StateArt } from './StateArt';
import constants from 'constants';
import {
  StatusPage,
  Card,
  ModelTitle,
  Headline,
  SubLine,
  MutedLine,
  Actions,
  SupportButton,
  ButtonContent,
  ShimmerTrack,
  ShimmerBar,
} from './styles';

const { JOB_PHASES, SUPPORT_URL } = constants;

const COPY = {
  [JOB_PHASES.RESOLVING]: {
    headline: 'Checking on your model',
    sub: 'Looking up where it is in the pipeline.',
  },
  [JOB_PHASES.WAITING_FOR_FILE]: {
    headline: 'Waiting for your file',
    sub: "The upload hasn't reached us yet. This page updates on its own.",
  },
  [JOB_PHASES.QUEUED]: {
    headline: 'In the queue',
    sub: 'Your model is waiting for a processing slot.',
  },
  [JOB_PHASES.PROCESSING]: {
    headline: 'Building your 3D model',
    sub: 'Usually takes a minute or two.',
  },
  [JOB_PHASES.COMPLETED]: { headline: 'Your model is ready' },
  [JOB_PHASES.READY]: { headline: 'Your model is ready' },
  [JOB_PHASES.FAILED]: {
    headline: "We couldn't process this model",
    sub: 'Something went wrong while converting your file.',
  },
  [JOB_PHASES.NOT_FOUND]: {
    headline: 'This model was not found',
    sub: 'It may have been deleted, or the link may be incorrect.',
  },
  [JOB_PHASES.UNAVAILABLE]: {
    headline: "We couldn't reach the model service",
    sub: 'This is usually temporary.',
  },
};

const STAGES = {
  [JOB_PHASES.RESOLVING]: 0,
  [JOB_PHASES.WAITING_FOR_FILE]: 0,
  [JOB_PHASES.QUEUED]: 1,
  [JOB_PHASES.PROCESSING]: 2,
  [JOB_PHASES.COMPLETED]: 3,
  [JOB_PHASES.READY]: 3,
};

const TERMINAL_PHASES = [
  JOB_PHASES.FAILED,
  JOB_PHASES.NOT_FOUND,
  JOB_PHASES.UNAVAILABLE,
];

const SUPPORT_TEXT = 'If you have any questions, please contact our support team.';

const ModelStatus = withTheme(props => {
  const {
    theme,
    phase,
    title,
    thumbnailURL,
    elapsedLabel,
    longWait,
    stalled,
    onOpen,
    onRetry,
    onCheckAgain,
  } = props;

  const appStore = useSelector(state => state.app);
  const selectedTheme = appStore.theme;
  const themeProps = { theme, $selectedTheme: selectedTheme };
  const lastStage = useRef(0);

  const terminal = TERMINAL_PHASES.includes(phase);
  const copy = COPY[phase] || COPY[JOB_PHASES.PROCESSING];

  // Terminal screens freeze the rail wherever the model got to.
  if (!terminal) lastStage.current = STAGES[phase] ?? 0;
  const stage = terminal ? lastStage.current : STAGES[phase] ?? 0;

  const inFlight = !terminal
    && phase !== JOB_PHASES.COMPLETED
    && phase !== JOB_PHASES.READY;

  const getSubLine = () => {
    if (inFlight && stalled) return 'This is taking much longer than usual.';
    return copy.sub;
  };

  const buildSupportButton = () => (
    <SupportButton
      {...themeProps}
      href={SUPPORT_URL}
      target='_blank'
      rel='noopener noreferrer'
    >
      Contact support
    </SupportButton>
  );

  const buildActions = () => {
    if (phase === JOB_PHASES.COMPLETED) {
      return (
        <Actions {...themeProps}>
          <FovButton disabled={true}>
            <ButtonContent {...themeProps}>
              <SpinAnimation duration={1200}>
                <LoadingIcon width={18} height={18} />
              </SpinAnimation>
              Preparing...
            </ButtonContent>
          </FovButton>
        </Actions>
      );
    }

    if (phase === JOB_PHASES.READY) {
      return (
        <Actions {...themeProps}>
          <FovButton text='Open model' callback={onOpen} />
        </Actions>
      );
    }

    if (inFlight && stalled) {
      return (
        <Actions {...themeProps}>
          <FovButton text='Check again' callback={onCheckAgain} />
          {buildSupportButton()}
        </Actions>
      );
    }

    // The jobs API exposes no failure reason, so a failed conversion is a dead
    // end — re-running it would change nothing.
    if (phase === JOB_PHASES.UNAVAILABLE) {
      return (
        <Actions {...themeProps}>
          <FovButton text='Try again' callback={onRetry} />
          {buildSupportButton()}
        </Actions>
      );
    }

    if (terminal) {
      return (
        <Actions {...themeProps}>
          {buildSupportButton()}
        </Actions>
      );
    }
  };

  const showSupportText = terminal || (inFlight && stalled);

  return (
    <StatusPage {...themeProps}>
      <Card {...themeProps}>
        <ModelTitle {...themeProps}>{title}</ModelTitle>

        <VerticalSpacer size={8} />

        <StateArt {...themeProps} phase={phase} thumbnailURL={thumbnailURL} />

        {phase === JOB_PHASES.PROCESSING && (
          <ShimmerTrack {...themeProps}>
            <ShimmerBar {...themeProps} />
          </ShimmerTrack>
        )}

        <VerticalSpacer size={24} />

        <ProgressRail {...themeProps} stage={stage} frozen={terminal} />

        <VerticalSpacer size={24} />

        <Headline {...themeProps}>{copy.headline}</Headline>

        {getSubLine() && (
          <>
            <VerticalSpacer size={8} />
            <SubLine {...themeProps}>{getSubLine()}</SubLine>
          </>
        )}

        {inFlight && longWait && !stalled && (
          <>
            <VerticalSpacer size={4} />
            <SubLine {...themeProps}>This is taking longer than usual.</SubLine>
          </>
        )}

        {showSupportText && (
          <>
            <VerticalSpacer size={8} />
            <SubLine {...themeProps}>{SUPPORT_TEXT}</SubLine>
          </>
        )}

        <VerticalSpacer size={24} />

        {buildActions()}

        {elapsedLabel && (
          <>
            <VerticalSpacer size={16} />
            <MutedLine {...themeProps}>{elapsedLabel}</MutedLine>
          </>
        )}
      </Card>
    </StatusPage>
  );
});

export { ModelStatus };
